import React, { useEffect, useMemo, useRef, useState } from "react";
import { Sparkles, X, Send, Loader2, Wand2, Check, Zap, ArrowRight, Image as ImageIcon, Upload, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { aiEmailApi, pipelineApi, automationsApi, marketingApi } from "../../lib/api";
import { renderEmailHtml, absUrl as abs } from "./emailBlocks";

const GOAL_OPTS = ["Promotion / offer", "Review request", "Re-engage lapsed customers", "Announcement", "Seasonal campaign", "Welcome / onboarding", "Newsletter"];
const TONE_OPTS = ["Friendly", "Professional", "Bold & urgent", "Warm & premium", "Minimal & clean"];
const AUDIENCE_OPTS = ["All customers", "Residential", "Commercial", "Lapsed customers"];

const chip = "px-3 py-1.5 rounded-full text-[12.5px] border transition-colors text-left";

function ColorChips({ accent, value, onPick }) {
  const opts = [
    { label: "Use brand color", hex: accent },
    { label: "Blue & clean", hex: "#2563EB" },
    { label: "Warm (orange)", hex: "#EA580C" },
    { label: "Green & fresh", hex: "#059669" },
    { label: "Purple & premium", hex: "#7C3AED" },
    { label: "Dark & bold", hex: "#0F172A" },
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {opts.map((o, i) => (
        <button key={o.label} data-testid={`ai-color-${i}`} onClick={() => onPick(o)}
          className={`${chip} inline-flex items-center gap-1.5 ${value === o.label ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"}`}>
          <span className="h-3 w-3 rounded-full border border-black/10" style={{ background: o.hex }} /> {o.label}
        </button>
      ))}
    </div>
  );
}

const EDIT_PRESETS = [
  "Remove the background and place the subject on a clean white background",
  "Brighten, sharpen and make the colors more vibrant",
  "Turn it into a modern flat vector illustration",
  "Crop and compose it as a wide email hero banner",
];

function ImageStep({ onDone }) {
  const [mode, setMode] = useState("choose"); // choose | generate | upload | preview
  const [busy, setBusy] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [instruction, setInstruction] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const fileRef = useRef(null);

  const doGenerate = async () => {
    if (!prompt.trim()) return;
    setBusy(true);
    try { const res = await aiEmailApi.generateImage(prompt.trim()); setPreview(abs(res.url)); setMode("preview"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Image generation failed."); }
    finally { setBusy(false); }
  };
  const doEdit = async () => {
    if (!file || !instruction.trim()) return toast.error("Choose an image and describe the edit.");
    setBusy(true);
    try { const res = await aiEmailApi.editImage(file, instruction.trim()); setPreview(abs(res.url)); setMode("preview"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Image edit failed."); }
    finally { setBusy(false); }
  };
  const useOriginal = async () => {
    if (!file) return;
    setBusy(true);
    try { const res = await marketingApi.uploadAsset(file); onDone(abs(res.url), "Using my uploaded image"); }
    catch { toast.error("Upload failed."); }
    finally { setBusy(false); }
  };

  if (mode === "choose") return (
    <div className="flex flex-wrap gap-2">
      <button data-testid="ai-chip-image-generate" onClick={() => setMode("generate")} className={`${chip} inline-flex items-center gap-1.5 border-slate-200 bg-white text-slate-700 hover:border-violet-400`}><Sparkles className="h-3.5 w-3.5" /> Generate from a description</button>
      <button data-testid="ai-chip-image-upload" onClick={() => setMode("upload")} className={`${chip} inline-flex items-center gap-1.5 border-slate-200 bg-white text-slate-700 hover:border-violet-400`}><Upload className="h-3.5 w-3.5" /> Upload & AI-edit</button>
      <button data-testid="ai-chip-image-skip" onClick={() => onDone("", "No image")} className={`${chip} border-dashed border-slate-300 text-slate-500`}>No image</button>
    </div>
  );
  if (mode === "generate") return (
    <div className="space-y-2">
      <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="ai-img-prompt" rows={2} placeholder="Describe the hero image — e.g. a friendly technician servicing an AC unit at a sunny suburban home"
        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] resize-none focus:outline-none focus:ring-2 focus:ring-violet-500/20" />
      <div className="flex gap-2">
        <Button onClick={doGenerate} disabled={busy || !prompt.trim()} data-testid="ai-img-generate-btn" className="flex-1 h-9 bg-violet-600 hover:bg-violet-700 text-white text-[13px]">
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Generate image
        </Button>
        <button onClick={() => setMode("choose")} className="h-9 px-3 rounded-lg text-[12.5px] text-slate-500 hover:bg-slate-100">Back</button>
      </div>
      {busy && <div className="text-[11.5px] text-slate-400">This can take 10-20s…</div>}
    </div>
  );
  if (mode === "upload") return (
    <div className="space-y-2">
      <input ref={fileRef} type="file" accept="image/*" data-testid="ai-img-file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      <button onClick={() => fileRef.current?.click()} className="w-full h-9 rounded-lg border border-dashed border-slate-300 text-[12.5px] text-slate-600 hover:border-violet-400 inline-flex items-center justify-center gap-1.5 truncate px-3">
        <Upload className="h-4 w-4 shrink-0" /> {file ? file.name : "Choose an image"}
      </button>
      {file && (
        <>
          <div className="flex flex-wrap gap-1.5">
            {EDIT_PRESETS.map((p, i) => (
              <button key={i} data-testid={`ai-img-preset-${i}`} onClick={() => setInstruction(p)} className={`${chip} text-[11.5px] ${instruction === p ? "border-violet-500 bg-violet-50 text-violet-700" : "border-slate-200 bg-white text-slate-600 hover:border-violet-300"}`}>{p.split(" and ")[0].split(",")[0]}</button>
            ))}
          </div>
          <input value={instruction} onChange={(e) => setInstruction(e.target.value)} data-testid="ai-img-instruction" placeholder="Or describe the edit…"
            className="w-full h-9 px-3 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-500/20" />
          <div className="flex gap-2">
            <Button onClick={doEdit} disabled={busy || !instruction.trim()} data-testid="ai-img-edit-btn" className="flex-1 h-9 bg-violet-600 hover:bg-violet-700 text-white text-[13px]">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Wand2 className="h-4 w-4 mr-1.5" />} Apply AI edit
            </Button>
            <button onClick={useOriginal} disabled={busy} data-testid="ai-img-useoriginal-btn" className="h-9 px-3 rounded-lg text-[12.5px] text-slate-600 hover:bg-slate-100">Use as-is</button>
          </div>
        </>
      )}
      <button onClick={() => setMode("choose")} className="text-[12px] text-slate-500 hover:text-slate-700">Back</button>
    </div>
  );
  return (
    <div className="space-y-2">
      <img src={preview} alt="AI preview" data-testid="ai-img-preview" className="w-full rounded-lg border border-slate-200" />
      <div className="flex gap-2">
        <Button onClick={() => onDone(preview, "Added a hero image ✨")} data-testid="ai-img-use-btn" className="flex-1 h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-[13px]"><Check className="h-4 w-4 mr-1.5" /> Use this image</Button>
        <button onClick={() => setMode(prompt ? "generate" : "upload")} data-testid="ai-img-retry-btn" className="h-9 px-3 rounded-lg text-[12.5px] text-slate-600 hover:bg-slate-100 inline-flex items-center gap-1"><RefreshCw className="h-3.5 w-3.5" /> Redo</button>
      </div>
    </div>
  );
}

// sessionStorage key so the transcript survives close/reopen inside the same
// browser tab. Cleared once the user applies the design or reloads the tab.
const SS_KEY = "ai-email-designer:state:v1";

function _loadPersisted() {
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

export default function AIEmailDesigner({ accent = "#2563EB", companyName = "", onApplyDesign, onClose }) {
  const persisted = _loadPersisted();
  const [transcript, setTranscript] = useState(persisted?.transcript || [{ role: "agent", text: "Hi! I'll design your email. First — what's the goal of this email?" }]);
  const [stepIdx, setStepIdx] = useState(persisted?.stepIdx ?? 0);
  const [answers, setAnswers] = useState(persisted?.answers || { goal: "", message: "", tone: "", colors: "", accent, ctaText: "", ctaUrl: "", audience: "", heroImageUrl: "" });
  const [textVal, setTextVal] = useState("");
  const [ctaText, setCtaText] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [phase, setPhase] = useState(persisted?.phase || "questions"); // questions | generating | done
  const [generated, setGenerated] = useState(persisted?.generated || null); // {name, subject, design}
  const scrollRef = useRef(null);

  // Panel height in pixels — starts at 30% of viewport, resizable via drag
  // handle on the top edge. Clamped between 15% and 85% of the viewport.
  const [panelPx, setPanelPx] = useState(() =>
    Math.round((typeof window !== "undefined" ? window.innerHeight : 900) * 0.30)
  );
  const draggingRef = useRef(false);

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return;
      const y = e.clientY || (e.touches && e.touches[0]?.clientY) || 0;
      const next = Math.max(window.innerHeight * 0.15, Math.min(window.innerHeight * 0.85, window.innerHeight - y));
      setPanelPx(Math.round(next));
    };
    const onUp = () => { draggingRef.current = false; document.body.style.userSelect = ""; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  // Persist conversation state on every relevant change
  useEffect(() => {
    try {
      sessionStorage.setItem(SS_KEY, JSON.stringify({ transcript, stepIdx, answers, phase, generated }));
    } catch { /* quota / private mode */ }
  }, [transcript, stepIdx, answers, phase, generated]);

  // Automations state
  const [autoLoading, setAutoLoading] = useState(false);
  const [proposals, setProposals] = useState(null);
  const [boards, setBoards] = useState([]);
  const [board, setBoard] = useState("");
  const [stages, setStages] = useState([]);
  const [stageId, setStageId] = useState("");
  const [creating, setCreating] = useState(false);
  const [createdCount, setCreatedCount] = useState(0);

  const STEPS = useMemo(() => ([
    { key: "goal", type: "chips", options: GOAL_OPTS, allowText: true, ph: "Or describe your own goal…" },
    { key: "message", type: "text", ph: "e.g. 20% off spring A/C tune-ups through May 31", q: "Great. What's the key message? Include any offer, dates, or details." },
    { key: "tone", type: "chips", options: TONE_OPTS, q: "What tone should it strike?" },
    { key: "colors", type: "color", q: "Pick a color scheme." },
    { key: "cta", type: "cta", q: "What should the button say, and where should it link? (optional)" },
    { key: "image", type: "image", q: "Want a hero image at the top? I can generate one from a description or edit an image you upload." },
    { key: "audience", type: "chips", options: AUDIENCE_OPTS, optional: true, q: "Last one — who's this for? (optional, helps tailor the copy)" },
  ]), []);

  const step = STEPS[stepIdx];

  useEffect(() => { scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" }); }, [transcript, phase, proposals]);

  const advance = (key, label, patch) => {
    setAnswers((a) => ({ ...a, ...patch }));
    setTranscript((t) => {
      const next = [...t, { role: "user", text: label }];
      const ni = stepIdx + 1;
      if (ni < STEPS.length) next.push({ role: "agent", text: STEPS[ni].q });
      return next;
    });
    setTextVal("");
    if (stepIdx + 1 < STEPS.length) setStepIdx(stepIdx + 1);
    else generate({ ...answers, ...patch });
  };

  const generate = async (brief) => {
    setPhase("generating");
    setTranscript((t) => [...t, { role: "agent", text: "Designing your email…", loading: true }]);
    try {
      const res = await aiEmailApi.design({ ...brief, companyName });
      setGenerated(res);
      onApplyDesign && onApplyDesign(res);
      setPhase("done");
      setTranscript((t) => [...t.filter((m) => !m.loading), { role: "agent", text: `Done! "${res.subject || res.name}" is on your canvas and fully editable. Want me to set up automations to send it automatically?` }]);
    } catch (e) {
      setPhase("questions");
      setTranscript((t) => t.filter((m) => !m.loading));
      toast.error(e?.response?.data?.detail || "The AI couldn't design that. Try rephrasing your message.");
    }
  };

  const loadBoards = async () => {
    try {
      const pls = await pipelineApi.pipelines().catch(() => []);
      const opts = [...pls.map((p) => ({ id: p.id, name: p.name })), { id: "sales", name: "Sales (estimates)" }, { id: "jobs", name: "Jobs" }];
      setBoards(opts);
      const first = opts[0]?.id || "";
      setBoard(first);
      if (first) loadStages(first);
    } catch { /* noop */ }
  };

  const loadStages = async (b) => {
    try {
      const st = await pipelineApi.stages(b);
      setStages(st);
      setStageId(st[0]?.id || "");
    } catch { setStages([]); setStageId(""); }
  };

  const suggestAutomations = async () => {
    setAutoLoading(true);
    try {
      const body = renderEmailHtml(generated.design);
      const res = await aiEmailApi.suggestAutomations({ goal: answers.goal, subject: generated.subject, bodyHtml: body });
      setProposals(res.automations || []);
      if ((res.automations || []).length === 0) toast.info("No automations suggested for this email.");
      await loadBoards();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't suggest automations.");
    } finally { setAutoLoading(false); }
  };

  const createDrafts = async () => {
    if (!board || !stageId) return toast.error("Pick a pipeline and stage first.");
    setCreating(true);
    let ok = 0; let firstErr = "";
    for (const p of proposals) {
      try {
        await automationsApi.create({
          name: p.name, board, stageId, trigger: p.trigger,
          triggerConfig: p.triggerConfig || {}, actions: p.actions, enabled: false,
        });
        ok += 1;
      } catch (e) { if (!firstErr) firstErr = e?.response?.data?.detail || "create failed"; }
    }
    setCreating(false);
    setCreatedCount(ok);
    if (ok === proposals.length) toast.success(`${ok} automation${ok > 1 ? "s" : ""} added as drafts in the Automations tab.`);
    else if (ok > 0) toast.warning(`Added ${ok} of ${proposals.length}. ${firstErr}`);
    else toast.error(firstErr || "Could not create automations. Check the selected pipeline/stage.");
  };

  const actionSummary = (a) => a.actions.map((x) => x.type.replace("_", " ")).join(" · ");

  return (
    <div
      className="fixed left-0 right-0 bottom-0 bg-white z-[130] shadow-2xl border-t border-slate-200 flex flex-col"
      style={{ height: `${panelPx}px` }}
      data-testid="ai-email-designer"
    >
      {/* Drag handle — grab the top edge to resize */}
      <div
        onMouseDown={() => { draggingRef.current = true; document.body.style.userSelect = "none"; }}
        onTouchStart={() => { draggingRef.current = true; }}
        data-testid="ai-email-resize"
        className="h-2 shrink-0 flex items-center justify-center cursor-ns-resize hover:bg-slate-200 transition-colors group"
        title="Drag to resize"
      >
        <div className="w-10 h-1 rounded-full bg-slate-300 group-hover:bg-slate-400" />
      </div>

      {/* Header */}
      <div className="h-12 shrink-0 px-4 flex items-center justify-between text-white" style={{ background: "linear-gradient(90deg, #7c3aed 0%, #2563eb 100%)" }}>
        <div className="flex items-center gap-2">
          <Wand2 className="h-5 w-5" color="#ffffff" style={{ color: "#ffffff" }} />
          <div className="font-semibold text-[14px] leading-tight" style={{ color: "#ffffff" }}>AI Email Designer</div>
        </div>
        <button onClick={onClose} data-testid="ai-email-close" title="Close (your conversation is kept)" className="h-9 w-9 rounded-md hover:bg-white/20 flex items-center justify-center"><X className="h-6 w-6 shrink-0" color="#ffffff" strokeWidth={2.5} style={{ color: "#ffffff", stroke: "#ffffff" }} /></button>
      </div>

      {/* Transcript */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
        {transcript.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed ${m.role === "user" ? "bg-blue-600 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-700 rounded-bl-sm"}`}>
              {m.loading ? <span className="inline-flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {m.text}</span> : m.text}
            </div>
          </div>
        ))}

        {/* Automations UI (after done) */}
        {phase === "done" && (
          <div className="pt-1 space-y-3">
            {proposals === null ? (
              <button onClick={suggestAutomations} disabled={autoLoading} data-testid="ai-suggest-automations"
                className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-lg bg-slate-900 text-white text-[13px] font-medium hover:bg-slate-800 disabled:opacity-60">
                {autoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />} Suggest automations
              </button>
            ) : proposals.length > 0 ? (
              <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-3" data-testid="ai-automations-panel">
                <div className="text-[12px] font-semibold text-slate-700">Recommended automations (created as disabled drafts)</div>
                {proposals.map((p, i) => (
                  <div key={i} data-testid={`ai-proposal-${i}`} className="rounded-lg border border-slate-100 bg-slate-50 p-2.5">
                    <div className="text-[12.5px] font-semibold text-slate-800">{p.name}</div>
                    <div className="text-[11.5px] text-slate-500 mt-0.5">{p.description}</div>
                    <div className="text-[10.5px] text-slate-400 mt-1 capitalize">{p.trigger.replace("_", " ")} → {actionSummary(p)}</div>
                  </div>
                ))}
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <div>
                    <label className="text-[10.5px] font-medium text-slate-500">Pipeline</label>
                    <select data-testid="ai-auto-board" value={board} onChange={(e) => { setBoard(e.target.value); loadStages(e.target.value); }}
                      className="w-full h-8 mt-0.5 px-2 rounded-md border border-slate-200 text-[12px] text-slate-900 bg-white">
                      {boards.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10.5px] font-medium text-slate-500">Stage</label>
                    <select data-testid="ai-auto-stage" value={stageId} onChange={(e) => setStageId(e.target.value)}
                      className="w-full h-8 mt-0.5 px-2 rounded-md border border-slate-200 text-[12px] text-slate-900 bg-white">
                      {stages.length === 0 && <option value="">No stages</option>}
                      {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                </div>
                {createdCount > 0 ? (
                  <div className="text-[12px] text-emerald-600 font-medium inline-flex items-center gap-1.5"><Check className="h-4 w-4" /> Added {createdCount} draft(s) to the Automations tab.</div>
                ) : (
                  <Button onClick={createDrafts} disabled={creating || !stageId} data-testid="ai-auto-create" className="w-full h-9 bg-violet-600 hover:bg-violet-700 text-white text-[13px]">
                    {creating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />} Add as drafts
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-[12px] text-slate-500">No automations suggested. You can add them anytime in the Automations tab.</div>
            )}
            <button onClick={onClose} data-testid="ai-email-done" className="w-full text-[12.5px] text-slate-500 hover:text-slate-700 py-1">Close designer</button>
          </div>
        )}
      </div>

      {/* Active step input */}
      {phase === "questions" && step && (
        <div className="shrink-0 border-t border-slate-200 p-3 bg-white space-y-2" data-testid={`ai-step-${step.key}`}>
          {step.type === "chips" && (
            <div className="flex flex-wrap gap-2">
              {step.options.map((o, i) => (
                <button key={o} data-testid={`ai-chip-${step.key}-${i}`} onClick={() => advance(step.key, o, { [step.key]: o })}
                  className={`${chip} border-slate-200 bg-white text-slate-700 hover:border-blue-400 hover:text-blue-700`}>{o}</button>
              ))}
              {step.optional && (
                <button data-testid={`ai-chip-${step.key}-skip`} onClick={() => advance(step.key, "Any audience", { [step.key]: "" })}
                  className={`${chip} border-dashed border-slate-300 text-slate-500`}>Skip</button>
              )}
            </div>
          )}
          {step.type === "color" && <ColorChips accent={accent} value={answers.colors} onPick={(o) => advance("colors", o.label, { colors: o.label, accent: o.hex })} />}
          {step.type === "image" && <ImageStep onDone={(url, label) => advance("image", label, { heroImageUrl: url })} />}
          {(step.type === "chips" && step.allowText) && (
            <div className="flex items-center gap-2 pt-1">
              <input value={textVal} onChange={(e) => setTextVal(e.target.value)} data-testid="ai-text-input" placeholder={step.ph}
                onKeyDown={(e) => { if (e.key === "Enter" && textVal.trim()) advance(step.key, textVal.trim(), { [step.key]: textVal.trim() }); }}
                className="flex-1 h-9 px-3 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
              <button onClick={() => textVal.trim() && advance(step.key, textVal.trim(), { [step.key]: textVal.trim() })} data-testid="ai-text-send"
                className="h-9 w-9 rounded-lg bg-blue-600 text-white flex items-center justify-center disabled:opacity-40" disabled={!textVal.trim()}><Send className="h-4 w-4" /></button>
            </div>
          )}
          {step.type === "text" && (
            <div className="flex items-center gap-2">
              <input value={textVal} onChange={(e) => setTextVal(e.target.value)} data-testid="ai-text-input" placeholder={step.ph}
                onKeyDown={(e) => { if (e.key === "Enter" && textVal.trim()) advance(step.key, textVal.trim(), { [step.key]: textVal.trim() }); }}
                className="flex-1 h-9 px-3 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
              <button onClick={() => textVal.trim() && advance(step.key, textVal.trim(), { [step.key]: textVal.trim() })} data-testid="ai-text-send"
                className="h-9 w-9 rounded-lg bg-blue-600 text-white flex items-center justify-center disabled:opacity-40" disabled={!textVal.trim()}><Send className="h-4 w-4" /></button>
            </div>
          )}
          {step.type === "cta" && (
            <div className="space-y-2">
              <input value={ctaText} onChange={(e) => setCtaText(e.target.value)} data-testid="ai-cta-text" placeholder="Button text — e.g. Book now"
                className="w-full h-9 px-3 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
              <input value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} data-testid="ai-cta-url" placeholder="Link URL — e.g. https://yoursite.com/book"
                className="w-full h-9 px-3 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
              <div className="flex gap-2">
                <Button onClick={() => advance("cta", ctaText.trim() ? `${ctaText.trim()} → ${ctaUrl.trim() || "link"}` : "Auto-pick CTA", { ctaText: ctaText.trim(), ctaUrl: ctaUrl.trim() })}
                  data-testid="ai-cta-continue" className="flex-1 h-9 bg-blue-600 hover:bg-blue-700 text-white text-[13px]">
                  Continue <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
